import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { GetSiteGQL, GetProfileGQL, GetProfilesGQL, CreateProfileGQL, UpdateProfileGQL } from '../../../graphql/graphql';
import { ToastService } from '../../../shared/toast/toast.service';
import { UploadService } from '../../../shared/upload/upload.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false
})
export class ProfileComponent implements OnInit {
  profileForm: FormGroup;
  isEdit = false;
  profileId: string | null = null;
  siteId: string = '';
  siteUrl: string = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private getSiteGQL: GetSiteGQL,
    private getProfileGQL: GetProfileGQL,
    private createProfileGQL: CreateProfileGQL,
    private updateProfileGQL: UpdateProfileGQL,
    private getProfilesGQL: GetProfilesGQL,
    private toast: ToastService,
    private titleService: Title,
    private uploadService: UploadService
  ) {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      surname: ['', Validators.required],
      title: [''],
      text: ['', Validators.required],
      image: [''],
      social: this.fb.group({
        facebook: [''],
        instagram: [''],
        linkedIn: [''],
        twitter: ['']
      })
    });
  }

  ngOnInit() {
    this.profileId = this.route.snapshot.params['id'];
    this.isEdit = !!this.profileId;
    this.siteUrl = this.route.parent?.snapshot.params['url'] ||
                   this.route.parent?.parent?.snapshot.params['url'] || '';
    this.loadSiteData();
  }

  loadSiteData() {
    if (this.siteUrl) {
      this.getSiteGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
        if (result.data?.site?._id) {
          this.siteId = result.data.site._id;
          if (this.isEdit) {
            this.loadProfileData();
          }
        }
      });
    }
  }

  loadProfileData() {
    if (this.profileId) {
      this.getProfileGQL.watch({ variables: { id: this.profileId } }).valueChanges.subscribe(result => {
        if (result.data?.getProfileById) {
          const profile = result.data.getProfileById;
          this.profileForm.patchValue({
            firstName: profile.firstName || profile.name?.split(' ')[0] || '',
            surname: profile.surname || profile.name?.split(' ').slice(1).join(' ') || '',
            title: profile.title || '',
            text: profile.text,
            image: profile.image?.src || '',
            social: {
              facebook: profile.social?.facebook || '',
              instagram: profile.social?.instagram || '',
              linkedIn: profile.social?.linkedIn || '',
              twitter: profile.social?.twitter || ''
            }
          });
          this.titleService.setTitle(`Profiles | ${profile.firstName || ''} ${profile.surname || ''} | Saltus Partners`);
        }
      });
    }
  }

  async uploadPhoto() {
    const path = `profiles/${this.profileId || 'new'}/`;
    const results = await this.uploadService.pick({ path });
    if (results.length) {
      this.profileForm.patchValue({ image: results[0].url });
    }
  }

  onSubmit() {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    if (this.siteId) {
      const formData = this.profileForm.value;
      const input = {
        name: `${formData.firstName} ${formData.surname}`.trim(),
        firstName: formData.firstName,
        surname: formData.surname,
        title: formData.title,
        text: formData.text,
        site: this.siteId,
        image: formData.image ? { src: formData.image } : null,
        social: formData.social
      };

      const refetchQueries = [{ query: this.getProfilesGQL.document, variables: { siteId: this.siteId } }];

      if (this.isEdit && this.profileId) {
        this.updateProfileGQL.mutate({ variables: { id: this.profileId, updateProfileInput: input }, refetchQueries }).subscribe({
          next: () => { this.toast.success('Profile updated'); this.navigateBack(); },
          error: (err) => { console.error('Error updating profile:', err); this.toast.error('Error updating profile'); }
        });
      } else {
        this.createProfileGQL.mutate({ variables: { createProfileInput: input }, refetchQueries }).subscribe({
          next: () => { this.toast.success('Profile created'); this.navigateBack(); },
          error: (err) => { console.error('Error creating profile:', err); this.toast.error('Error creating profile'); }
        });
      }
    }
  }

  onCancel() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.isEdit) {
      this.router.navigate(['../../'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
  }
}
